import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    List,
    ListItem,
    ListItemAvatar,
    Avatar,
    ListItemText,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Accordion,
    IconButton,
} from "@mui/material";
import {
    Delete,
    Add,
    Edit,
    Category as CategoryIcon
} from "@mui/icons-material";
import apiClient from "../utils/apiClient";
import { toast } from "react-toastify";
import { getImageFullUrl } from "../utils/common";
import FallbackImage from "../components/FallbackImage";
import ConfirmDialog from "../components/ConfirmDialog";

const BrandManager = () => {
    const [categories, setCategories] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [mode, setMode] = useState("");
    const [formData, setFormData] = useState({
        id: "",
        name: "",
        parentId: "",
        iconFile: null,
        iconPreview: "",
        level: 0
    });

    // ✅ Fetch categories
    const fetchCategories = async () => {
        try {
            const res = await apiClient.get("/api/brand");
            setCategories(res?.data?.data || []);
        } catch (err) {
            toast.error("Failed to load brands");
        }
    };

    // ✅ Delete category
    const deleteCategory = async (id) => {
        try {
            await apiClient.delete(`/api/brand/${id}`);
            toast.success("Brand deleted");
            fetchCategories();
        } catch (err) {
            toast.error("Failed to delete brand");
        }
    };


    // ✅ Confirm dialog state
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [deleteData, setDeleteData] = useState({});

    // ✅ Show confirm dialog before delete
    const requestDelete = (id) => {
        setDeleteData(id);
        setConfirmOpen(true);
    };

    // ✅ Open Add/Edit dialog
    const handleOpenDialog = (category = null, parent = null, level = 0) => {
        if (category) {
            // Editing
            setFormData({
                id: category.id,
                name: category.name,
                parentId: category.parentId || "",
                iconFile: null,
                iconPreview: getImageFullUrl(category.icon) || "",
                level
            });
            setMode("edit");
        } else {
            // Adding
            setFormData({
                id: "",
                name: "",
                parentId: parent ? parent.id : "",
                iconFile: null,
                iconPreview: "",
                level: parent ? level + 1 : 0
            });
            setMode("add");
        }
        setDialogOpen(true);
    };

    const handleCloseDialog = () => setDialogOpen(false);

    // ✅ Handle file select
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(file.type)) {
                toast.error("Only JPG, PNG, or WEBP images are allowed!");
                return;
            }
            setFormData({
                ...formData,
                iconFile: file,
                iconPreview: URL.createObjectURL(file)
            });
        }
    };

    // ✅ Save category (create/update)
    const handleSaveCategory = async () => {
        if (!formData.name) {
            toast.error("Name is required");
            return
        }
        setLoading(true)
        try {
            const data = new FormData();
            data.append("name", formData.name);
            if (formData.parentId) data.append("parentId", formData.parentId);
            if (formData.iconFile) data.append("icon", formData.iconFile);

            if (mode === "edit") {
                await apiClient.put(`/api/brand/${formData.id}`, data, {
                    headers: { "Content-Type": "multipart/form-data" }
                });
                toast.success("Brand updated");
            } else if (mode === "add") {
                await apiClient.post("/api/brand", data, {
                    headers: { "Content-Type": "multipart/form-data" }
                });
                toast.success("Brand created");
            }
            handleCloseDialog();
            fetchCategories();
        } catch (err) {
            toast.error("Failed to save brand");
        }finally{
            setLoading(false)
        } 
    };

    // ✅ Recursive render
    const renderCategory = (category, level = 0) => (
        <Accordion key={category.id} sx={{ border: 'solid 2px', padding:'5px', marginBottom:'10px'
         }}>
                <ListItem
                    sx={{ pl: level * 3 }}
                    secondaryAction={
                        <>
                            {/* Edit Button (Super category cannot change parent) */}
                            <IconButton
                                edge="end"
                                onClick={() => handleOpenDialog(category, null, level)}
                            >
                                <Edit color="primary" />
                            </IconButton>

                            {/* Delete */}
                            <IconButton edge="end" onClick={() => requestDelete(category)}>
                                <Delete color="error" />
                            </IconButton>
                        </>
                    }
                >
                    <ListItemAvatar>
                        <Avatar src={getImageFullUrl(category.icon)} alt={category.name}>
                            <CategoryIcon />
                        </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary={category.name} />
                </ListItem>
        </Accordion>
    );

    useEffect(() => {
        fetchCategories();
    }, []);

    return (
        <Box>

            <Card >
                <CardContent>
                    <Typography variant="h5" gutterBottom>
                        Brand Management
                    </Typography>

                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => handleOpenDialog()}
                    >
                        Add  Brand
                    </Button>
                    <List>
                        {categories.length > 0 ? (
                            categories?.map((cat) => renderCategory(cat))
                        ) : (
                            <Typography>No brand found</Typography>
                        )}
                    </List>
                </CardContent>
            </Card>

            <Dialog open={dialogOpen} onClose={handleCloseDialog} fullWidth>
                <DialogTitle>
                    {mode === "edit" ? "Edit Brand" : "Add Brand"}
                </DialogTitle>
                <DialogContent dividers
                    sx={{ display:  "flex",  flexDirection: "column", gap: 2, mt: 1, paddingTop:'10px'}}
                >
                    <TextField
                        label="Brand Name"
                        value={formData.name}
                        onChange={(e) =>
                            setFormData({ ...formData, name: e.target.value })
                        }
                        fullWidth
                    />

                    <Button variant="outlined" component="label">
                        Upload Icon
                        <input hidden type="file" accept="image/*" onChange={handleFileChange} />
                    </Button>
                    {formData.iconPreview && (
                        <Box sx={{ mt: 1 }}>
                            <FallbackImage
                                src={formData.iconPreview}
                                alt="preview"
                                style={{ maxWidth: "100px", borderRadius: "8px" }}
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button disabled={loading} variant="contained" onClick={handleSaveCategory}>
                        {mode === "edit" ? "Update" : "Create"}
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={confirmOpen}
                title={`Delete Brand - ${deleteData?.name}`}
                content="Are you sure you want to delete this brand?"
                onClose={() => setConfirmOpen(false)}
                onConfirm={() => {
                    deleteCategory(deleteData?.id)
                    setConfirmOpen(false)
                }}
            />
        </Box>
    );
};

export default BrandManager;
